import { useEffect, useRef, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import Typography from "../typography/typography";
import { Box } from "@mui/material";
import { LinearProgress } from "@mui/material";
import "./upload.scss";
import { useDropzone } from "react-dropzone";
import Toast from '../toast/toast';
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
} from "@mui/material";
import RemoveIcon from "@mui/icons-material/Remove";
import { Add } from "@mui/icons-material";
import Button from "../button/button";
import Checkbox from "../checkbox/checkbox";
import Avatar from "../avatar/avatar";
import images from "../../assets/images";
import Chip from "../chip/chip";
import { auth } from "../../constants/auth";
import { KeyboardArrowDown, KeyboardArrowUp, Clear } from "@mui/icons-material";
import FileConfirmationDialog from "./file-confirmation"
import { getApiConfig } from "@/utils";

interface UploadProps {
  open: boolean;
  title: string;
  multiple?: boolean;
  accept?: string;
  onClose: () => void;
  onConfirm: () => void;
  onSubmit: (files: any[]) => void;
  existingDriveData?: any;
  fileUploaded?: (value: boolean) => void | undefined;
  type: any


}
interface CustomNavigator extends Navigator {
  connection?: {
    downlink?: number;
  };
}
const formatBytes = (bytes: number, decimals = 2) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
};

const Upload = (props: UploadProps) => {
  const {
    open,
    title,
    onClose,
    onConfirm,
    accept = "*",
    onSubmit,
    multiple = true,
    existingDriveData,
    fileUploaded,
    type

  } = props;

  const [files, setFiles] = useState<
    {
      file: File;
      isPrivate: boolean;
      checkboxDisabled: boolean;
      status: string;
      uploadProgress: number;
      fileSize: number;
      fileName: string;
      fileType: string;
      location: string;
      message: string;
    }[]
  >([]);

  interface IToast {
    type: 'normal' | 'alert';
    message: string;
  }
  const [isUploading, setIsUploading] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isDialogMinimized, setIsDialogMinimized] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [abortController, setAbortController] = useState(new AbortController());
  const authToken = localStorage.getItem(auth.storageTokenKeyName);
  const [allDriveData, setAllDriveData] = useState();
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [selectedFileTitle, setSelectedFileTitle] = useState();
  const [toast, setToast] = useState<IToast | null>(null);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDropAccepted: (newFiles) => {
      setFiles([
        ...files,
        ...newFiles.map((file) => ({
          file,
          isPrivate: false,
          checkboxDisabled: false,
          status: "pending",
          uploadProgress: 0,
          fileSize: file.size,
          fileName: file.name,
          fileType: file.type,
          location: "",
          message: "",
        })),
      ]);
      setIsDragOver(false);
    },
    onDragEnter: () => {
      setIsDragOver(true);
    },
    onDragLeave: () => {
      setIsDragOver(false);
    },
    noClick: true,
    noKeyboard: true,
    multiple: multiple,
    accept: accept ? convertAcceptObject(accept) : accept,
  });

  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const driveTitles = existingDriveData?.map(item => item['title'])
    setAllDriveData(driveTitles);
  }, [existingDriveData]);

  useEffect(() => {
    const handleBeforeUnload = (event: any) => {
      if (isUploading) {
        const message =
          "Are you sure you want to leave? Your uploads are not complete.";
        event.returnValue = message; // Standard for most browsers
        return message; // For some older browsers
      }
    };
    if (fileInputRef.current) {
      fileInputRef.current.multiple = multiple;
    }

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      // Clean up the event listener when the component unmounts
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isUploading, multiple]);

  const getInternetSpeed = () => {
    const customNavigator = navigator as CustomNavigator;
    if ("connection" in customNavigator && customNavigator.connection) {
      return customNavigator.connection.downlink;
    } else {
      return null;
    }
  };

  function convertAcceptObject(types: string) {
    const accepts = types.split(",").map((item) => item.trim());
    const acceptObject = accepts.reduce((acc: any, type: string) => {
      acc[type] = [];
      return acc;
    }, {});
    return acceptObject;
  }

  const renderFileIcon = (file: any) => {
    const fileType = file?.type.split("/")[0];
    switch (fileType) {
      case "image":
        return (
          <div
            className={
              isDialogMinimized ? "document-icon-sm" : "document-icon-lg"
            }
            style={{ backgroundColor: "#FFEBEB" }}
          >
            <Avatar size={20} src={images.upload.documentPDF}></Avatar>
          </div>
        );
      case "application":
        return (
          <div
            className={
              isDialogMinimized ? "document-icon-sm" : "document-icon-lg"
            }
            style={{ backgroundColor: "#FFEBEB" }}
          >
            <Avatar size={20} src={images.upload.documentPDF}></Avatar>
          </div>
        );
      case "text":
        return (
          <div
            className={
              isDialogMinimized ? "document-icon-sm" : "document-icon-lg"
            }
            style={{ backgroundColor: "#EBF9F2" }}
          >
            <Avatar size={20} src={images.upload.docFile}></Avatar>
          </div>
        );
      default:
        return (
          <div
            className={
              isDialogMinimized ? "document-icon-sm" : "document-icon-lg"
            }
            style={{ backgroundColor: "#EBF9F2" }}
          >
            <Avatar size={20} src={images.upload.docFile}></Avatar>
          </div>
        );
    }
  };

  const BASE_URL = `${getApiConfig().backendBaseUrl}/document/v1/file-upload/`;

  const handleConfirm = async () => {
    setIsUploading(true);
    // Create a new AbortController for each confirmation
    const controller = new AbortController();
    setAbortController(controller);

    const filteredFiles = files.filter((file: any) => file.status !== "canceled").length;
    for (let i = 0; i < files.length; i++) {
      if (files[i].uploadProgress === 100 || files[i].status !== "pending") {
        continue;
      }

      const file = files[i];

      const fileSize = file?.fileSize;
      try {
        if (fileSize > 10 * 1024 * 1024) {
          const uploadResponse = await handleMultipartUpload(file, i, controller);
          if (uploadResponse) {
            setTimeout(() => { fileUploaded(true) }, 1000);
          }
        } else {
          await handleRegularUpload(file, i, controller, filteredFiles);
        }
      } catch (error: any) {
        setFiles((prevFiles) => {
          const updatedFiles = [...prevFiles];
          updatedFiles[i] = {
            ...updatedFiles[i],
            status: "failed",
            message: error.message || "Internal Server Error",
          };
          return updatedFiles;
        });
      } finally {
        setIsUploading(false);
      }
    }
  };




  let tempCount = 0;
  const handleRegularUpload = async (
    file: any,
    index: number,
    controller: any,
    fileCount: number
  ) => {
    try {
      const format = ['docx', 'doc', 'csv', 'pdf', 'xls', 'xlsx', 'vnd.ms-excel', 'excel', 'txt'];
      const mimeType = file?.mimeType || file?.fileType || '';
      const isValid = format.some(f => mimeType.includes(f));

      if (isValid && file?.fileSize && file?.fileSize >= 5242880) {
        return setToast({
          type: 'alert',
          message: 'File size must be within 5 MB!'
        });
      }
      const preSignedUrlsResponse = await fetch(
        `${BASE_URL}?key=${file?.fileName}&partCount=1&mimeType=${file?.fileType}&is_private=${!file?.isPrivate}`,
        {
          headers: {
            "Content-Type": "application/json",
            "file-size": file?.fileSize,
            "x-token": authToken,
          },
          signal: controller.signal
        },
      );
      const preSignedUrlsData = await preSignedUrlsResponse.json();
      if (preSignedUrlsData?.status_code == 400) {
        // alert(preSignedUrlsData?.message);
        setToast({
          type: 'alert',
          message: preSignedUrlsData?.message
        });
        return;
      }
      const preSignedUrl = preSignedUrlsData.data?.preSignedData?.presignedUrl;
      const location = preSignedUrlsData.data?.preSignedData?.location;
      const partParams = {
        Body: file?.file.slice(0, file?.fileSize),
        url: preSignedUrl,
      };
      const contentLength = file.fileSize;
      // const maxChunkSize = 1024 * 1024 * 1024; // 10 MB maximum chunk size
      // const chunkSize = Math.min(contentLength * 0.1, maxChunkSize);
      const chunkSize = 1024 * 1024 * 1024 * 1024 // 1MB chunk size
      let bytesUploaded = 0;

      while (bytesUploaded < contentLength) {
        const start = bytesUploaded;
        const end = Math.min(bytesUploaded + chunkSize, contentLength);

        // Send chunk of the file
        await fetch(partParams.url, {
          method: "PUT",
          body: file.file.slice(start, end),
          headers: {
            "Content-Type": "application/octet-stream",
          },
        });

        // Update progress
        bytesUploaded = end;
        const progress = Math.round((bytesUploaded / contentLength) * 100);

        setFiles((prevFiles) => {
          const updatedFiles = [...prevFiles];
          updatedFiles[index] = {
            ...updatedFiles[index],
            checkboxDisabled: true,
            status: "uploading",
            uploadProgress: progress,
          };
          return updatedFiles;
        });
      }

      setFiles((prevFiles) => {
        const updatedFiles = [...prevFiles];
        updatedFiles[index] = {
          ...updatedFiles[index],
          status: "completed",
          location,
          message: "Uploaded Successfully",
        };
        tempCount = tempCount + 1;

        if (fileCount == tempCount) {
          onSubmit(updatedFiles);
          tempCount = 0;

        }
        return updatedFiles;
      });
    } catch (error: any) {
      setFiles((prevFiles) => {
        const updatedFiles = [...prevFiles];
        updatedFiles[index] = {
          ...updatedFiles[index],
          status: "failed",
          message: error.message || "Internal Server Error",
        };
        return updatedFiles;
      });
    }

  };

  const handleMultipartUpload = async (
    file: any,
    index: number,
    controller: any,
  ) => {

    try {
      const partSize =
        (getInternetSpeed() || 10) * 1024 * 1024 < 5 * 1024 * 1024
          ? 5 * 1024 * 1024
          : (getInternetSpeed() || 10) * 1024 * 1024;
      const parts = Math.ceil(file?.fileSize / partSize);
      const preSignedUrlsResponse = await fetch(
        `${BASE_URL}?key=${file?.fileName}&partCount=${parts}&mimeType=${file?.fileType}&is_private=${!file?.isPrivate}`,
        {
          headers: {
            "Content-Type": "application/json",
            "file-size": file?.fileSize,
            "x-token": authToken,
          },
          signal: controller.signal,
        },
      );
      const preSignedUrlsData = await preSignedUrlsResponse.json();

      if (preSignedUrlsData && preSignedUrlsData?.status_code && preSignedUrlsData?.status_code == 400) {
        setToast({ message: preSignedUrlsData?.message || "Internal Server Error", type: "error" });
        return null;
      }

      const preSignedUrls = preSignedUrlsData.data.preSignedData?.preSignedUrls;
      if (preSignedUrls.length > 0) {
        const uploadId =
          preSignedUrls[0].presignedUrl.match(/uploadId=([^&]+)/)[1];
        for (let i = 0; i < preSignedUrls.length; i++) {
          const partNum = preSignedUrls[i].partNumber;
          const preSignedUrl = preSignedUrls[i].presignedUrl;
          const start = (partNum - 1) * partSize;
          const end = Math.min(start + partSize, file?.fileSize);
          const partParams = {
            Body: file?.file?.slice(start, end),
            url: preSignedUrl,
          };
          await fetch(partParams.url, {
            method: "PUT",
            body: partParams.Body,
            headers: {
              "Content-Type": "application/octet-stream",
            },
          });
          const progress = Math.round(((i + 1) * 100) / parts);
          setFiles((prevFiles) => {
            const updatedFiles = [...prevFiles];
            updatedFiles[index] = {
              ...updatedFiles[index],
              checkboxDisabled: true,
              status: "uploading",
              uploadProgress: progress,
            };
            return updatedFiles;
          });
        }
        const markCompleteResponse = await fetch(`${BASE_URL}mark-complete`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-token": authToken,
          },
          body: JSON.stringify({
            fileName: file?.fileName,
            uploadId: uploadId,
          }),
        });
        const markCompleteData = await markCompleteResponse.json();
        const location = markCompleteData?.data?.location;
        setFiles((prevFiles) => {
          const updatedFiles = [...prevFiles];
          updatedFiles[index] = {
            ...updatedFiles[index],
            status: "completed",
            location: location,
            message: "Uploaded Successfully",
          };

          onSubmit(updatedFiles);
          return updatedFiles;
        });
      } else {
        setFiles((prevFiles) => {
          const updatedFiles = [...prevFiles];
          updatedFiles[index] = {
            ...updatedFiles[index],
            status: "failed",
            message: "Did not get pre-signed URLs",
          };
          return updatedFiles;
        });
      }
    } catch (error: any) {
      setFiles((prevFiles) => {
        const updatedFiles = [...prevFiles];
        updatedFiles[index] = {
          ...updatedFiles[index],
          status: "failed",
          message: error.message || "Internal Server Error",
        };
        return updatedFiles;
      });
    }
  };

  const handleChipClick = () => {
    if (fileInputRef.current) {
      // If multiple is true, set input for multiple files
      if (props.multiple) {
        fileInputRef.current.setAttribute("multiple", "true");
      } else {
        // If multiple is false, set input for a single file
        fileInputRef.current.removeAttribute("multiple");
      }
      fileInputRef.current.click();
    }
  };

  //checking if the file already exists or not in the drive 
  const handleFileChange = (e: any) => {

    const selectedFiles = e.target.files;

    const fileTitles = Array.from(selectedFiles).map(file => {
      const fileName = file.name;
      const nameWithoutExtension = fileName.substring(0, fileName.lastIndexOf('.')) || fileName;
      return nameWithoutExtension;
    })[0];

    setSelectedFileTitle(fileTitles);

    setIsConfirmDialogOpen(allDriveData?.includes(fileTitles));
    const newFiles = Array.from(selectedFiles).map((file: any) => ({
      file,
      isPrivate: false,
      checkboxDisabled: false,
      status: "pending",
      uploadProgress: 0,
      fileSize: file?.size,
      fileName: file?.name,
      fileType: file?.type,
      location: "",
      message: "",
    }));
    setFiles((prevFiles: any) => [...prevFiles, ...newFiles]);
    setIsUploading(false);


  };

  const removeFile = (indexToRemove: number) => {

    setFiles((prevFiles) =>
      prevFiles.map((file, index) =>
        index === indexToRemove ? { ...file, status: "canceled" } : file,
      ),
    );

  };



  const toggleIsPrivate = (index: number) => {
    setFiles((prevFiles) =>
      prevFiles.map((item, i) =>
        i === index ? { ...item, isPrivate: !item.isPrivate } : item,
      ),
    );
  };

  const toggleCollapse = () => {
    setIsCollapsed((prevIsCollapsed) => !prevIsCollapsed);
  };

  const handleMinimizeClick = () => {
    setIsDialogMinimized(!isDialogMinimized);
    isDialogMinimized ? onConfirm() : onClose();
  };

  const handleDialogClose = () => {
    if (files.length > 0) {
      if (
        confirm(
          "Your uploads are not complete. Would you like to cancel all ongoing uploads?",
        )
      ) {
        // Abort the ongoing requests when the dialog is closed
        abortController.abort();
        if (abortController?.signal?.aborted) {
          onClose();
        }
        setFiles([]);
      }
    } else {
      onClose();
    }
  };
  useEffect(() => {
    open && setFiles([]);
  }, [open]);

  const onCloseConfirmPopup = () => {
    setIsConfirmDialogOpen(false);
  }

  const onReplace = async () => {
    const baseUrl = `${getApiConfig().backendBaseUrl}`;
    const fileId = existingDriveData?.filter(file => file.title === selectedFileTitle)[0].id;
    const headers = {
      "Content-type": "application/json; charset=utf-8",
    };
    const authToken = localStorage.getItem(auth.storageTokenKeyName) || '';
    headers["x-token"] = authToken;
    await fetch(`${baseUrl}/document/v1/drive/${fileId}`, {
      method: "DELETE",
      headers,
    });

    onCloseConfirmPopup();
  }

  const onKeepBoth = () => {
    onCloseConfirmPopup();
  }
  return (
    <>
      <Dialog open={open} onClose={onClose} className="upload">
        <DialogTitle className="upload--head">
          <Box className="upload--title">
            <Typography
              type="s3"
              ml={1}
              weight="medium"
              color="theme.secondary.1000"
            >
              {title}
            </Typography>
          </Box>
          <div>
            <IconButton disableRipple onClick={handleMinimizeClick}>
              <RemoveIcon />
            </IconButton>
            <IconButton disableRipple onClick={handleDialogClose}>
              <CloseIcon />
            </IconButton>
          </div>
        </DialogTitle>
        <Divider />
        <DialogContent className="upload--content">
          {files.length === 0 ? (
            <div
              className={`upload--fileContent ${isDragActive ? "drag-active" : ""
                }`}
            >
              <div {...getRootProps()} className="drop-view">
                {!isDragOver ? (
                  <>
                    <input {...getInputProps()} ref={fileInputRef} />
                    <p className="upload--text">
                      Drag and Drop files here or upload from
                    </p>

                    <div className="device-wrap">
                      <div className="select-button" onClick={handleChipClick}>
                        <img src={images.upload.monitor} />
                        <Typography type="s5">My Device</Typography>
                        <input
                          type="file"
                          {...getInputProps({ multiple: true })}
                          ref={fileInputRef}
                          style={{ display: "none" }}
                          onChange={async () => {
                            // handleFileChange(e) ; 
                            handleConfirm();
                          }}
                          accept={accept}
                        />
                      </div>
                      <div className="select-button" onClick={handleChipClick}>
                        <img src={images.upload.camera} />
                        <Typography type="s5">Camera</Typography>
                        <input
                          type="file"
                          {...getInputProps({ multiple: true })}
                          ref={fileInputRef}
                          style={{ display: "none" }}
                          onChange={handleFileChange}
                          accept={accept}
                        />
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="after-drop">
                    <img src={images.upload.documentUpload} />
                    <Typography type="s3">
                      Drag & Drop your files here
                    </Typography>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="main-container">
              <div className="body-section">
                <div className="document-section">
                  <div className="document-body">
                    {files.map((file: any, index: number) => (
                      <div className="upload-list" key={index}>
                        <div className="document-cover" key={file?.file?.name}>
                          <div className="document-name">
                            {renderFileIcon(file?.file)}
                            <div className="document-title">
                              <div className="document-box">
                                <div className="document-desc">
                                  {file?.file?.name}
                                </div>
                                <div className="document-size">
                                  {formatBytes(file?.file?.size)}
                                  <span
                                    className={
                                      file?.status === "completed" &&
                                        file?.message.length > 0
                                        ? "success-file"
                                        : "fail-file"
                                    }
                                  >
                                    {file?.message}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="document-choices">
                            {file?.status !== "canceled" ? (
                              <>
                                <div className="checkbox">
                                  <Checkbox
                                    checked={file?.isPrivate || type == 'public'}
                                    onChange={() => toggleIsPrivate(index)}
                                    disabled={file?.checkboxDisabled || type == 'public'}
                                  />
                                  <div className="checkbox-text">Public</div>
                                </div>
                                <div
                                  className="delete-icon"
                                  onClick={() => removeFile(index)}
                                >
                                  <Avatar
                                    size={24}
                                    src={images.upload.trash}
                                  ></Avatar>
                                </div>
                              </>
                            ) : (
                              <Typography type="s4" className="canceled-text">
                                Upload Canceled
                              </Typography>
                            )}
                          </div>
                        </div>
                        <div className="document-progress">
                          {file?.uploadProgress > 0 && (
                            <LinearProgress
                              color="success"
                              variant="determinate"
                              value={file?.uploadProgress}
                            />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                {multiple && (
                  <>
                    <Chip
                      className="add-file"
                      variant="outlined"
                      type="normal"
                      label={"Add File"}
                      icon={<Add />}
                      onClick={handleChipClick}
                    />
                    <input
                      type="file"
                      {...getInputProps({ multiple: true })}
                      ref={fileInputRef}
                      style={{ display: "none" }}
                      onChange={handleFileChange}
                      accept={accept}
                    />
                  </>
                )}
              </div>
            </div>
          )}
        </DialogContent>
        <Divider />
        <DialogActions className="upload--actions">
          <Button variant="text" onClick={handleDialogClose}>
            Cancel
          </Button>
          <Button
            color="secondary"
            onClick={handleConfirm}
            disabled={!files.length || isUploading}
          >
            Upload
          </Button>
        </DialogActions>
      </Dialog>
      {isDialogMinimized && (
        <div className="bottom-corner-box">
          <div className="top-section">
            <div className="top-text">
              Uploading {files.length} Item{files.length !== 1 ? "s" : ""}
            </div>
            <div className="top-options">
              <div className="btn" onClick={handleMinimizeClick}>
                <RemoveIcon />
              </div>
              <div className="btn" onClick={toggleCollapse}>
                <div className="arrow-btn">
                  {isCollapsed ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                </div>
              </div>
              <div className="btn" onClick={handleDialogClose}>
                <Clear />
              </div>
            </div>
          </div>
          <div className="box-list">
            {!isCollapsed &&
              files.map((file: any, index: number) => (
                <div className="bottom-section">
                  <div className="doc-icon">{renderFileIcon(file?.file)}</div>
                  <div className="file-detail">
                    <div className="file-name">
                      <div className="file-text">
                        {file?.file?.name}
                        {index}
                      </div>
                    </div>
                    <div className="file-status">
                      {file?.status !== "canceled" ? (
                        <>
                          {file.uploadProgress === 0 && (
                            <div className="avatar-container">
                              <Avatar
                                size={20}
                                className="avatar"
                                src={images.upload.empty}
                              ></Avatar>
                              <Avatar
                                size={20}
                                onClick={() => removeFile(index)}
                                src={images.upload.close}
                                className="close-icon"
                              ></Avatar>
                            </div>
                          )}
                          {file?.uploadProgress > 0 &&
                            file?.uploadProgress < 100 && (
                              <CircularProgress
                                color="success"
                                variant="determinate"
                                value={file?.uploadProgress}
                              />
                            )}
                          {file?.uploadProgress === 100 && (
                            <div className="avatar-container">
                              <Avatar
                                size={20}
                                className="avatar"
                                src={images.upload.tick}
                              ></Avatar>
                              <Avatar
                                size={20}
                                src={images.upload.folder}
                                className="close-icon"
                              ></Avatar>
                            </div>
                          )}
                        </>
                      ) : (
                        <Typography type="s4" className="canceled-text">
                          Upload Canceled
                        </Typography>
                      )}
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}
      {isConfirmDialogOpen && <FileConfirmationDialog title={selectedFileTitle} open={isConfirmDialogOpen} onClose={onCloseConfirmPopup} onReplace={onReplace} onKeepBoth={onKeepBoth} isFile={true} />}

      <Toast
        open={Boolean(toast)}
        type={toast?.type}
        message={toast?.message}
        handleClose={() => setToast(null)}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      />
    </>
  );
};

export { Upload };
export default Upload;
